import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Breadcrumbs,
  Link,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Group as GroupIcon,
  AttachMoney as RevenueIcon,
  Hotel as OccupancyIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CompareArrows as TransferIcon,
  Map as MapIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as StableIcon,
  NavigateNext as NavigateNextIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const LocationsPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [locationDashboard, setLocationDashboard] = useState<any>(null);
  const [comparativeAnalytics, setComparativeAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  
  // Dialogs
  const [locationDialog, setLocationDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [performanceDialog, setPerformanceDialog] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Form state
  const [locationForm, setLocationForm] = useState({
    name: '',
    shortName: '',
    code: '',
    type: 'RV Park',
    status: 'Active',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    },
    contact: {
      phone: '',
      email: '',
      website: ''
    },
    timezone: 'America/New_York',
    capacity: {
      totalSites: 0,
      rvSites: 0,
      tentSites: 0,
      cabins: 0
    },
    pricing: {
      taxRate: 0
    }
  });

  // Transfer form
  const [transferForm, setTransferForm] = useState({
    siteId: '',
    fromLocationId: '',
    toLocationId: '',
    transferDate: dayjs()
  });

  useEffect(() => {
    fetchLocations();
    if (user?.role === 'admin') {
      fetchComparativeAnalytics();
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    if (selectedLocation) {
      fetchLocationDashboard(selectedLocation._id);
    }
  }, [selectedLocation]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      
      const response = await api.get(`/locations?${params}`);
      
      // Handle both success wrapper and direct data response
      const locations = response.data?.success ? response.data.data : response.data;
      setLocations(Array.isArray(locations) ? locations : []);
      
      // Auto-select first location if none selected
      if (!selectedLocation && locations?.length > 0) {
        setSelectedLocation(locations[0]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationDashboard = async (locationId: string) => {
    try {
      setDashboardLoading(true);
      const response = await api.get(`/locations/${locationId}/dashboard`);
      setLocationDashboard(response.data);
    } catch (error) {
      console.error('Error fetching location dashboard:', error);
      // Set fallback data to show something instead of nothing
      setLocationDashboard({
        location: { name: 'Unknown Location' },
        metrics: {
          totalSites: 0,
          occupiedSites: 0,
          occupancyRate: 0,
          todayArrivals: 0,
          todayDepartures: 0,
          maintenanceIssues: 0,
          pendingTasks: 0,
          todayRevenue: 0,
          staffOnDuty: 0
        },
        alerts: [],
        recentActivity: []
      });
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchComparativeAnalytics = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      
      const response = await api.get(
        `/locations/analytics/comparative`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }
        }
      );
      setComparativeAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching comparative analytics:', error);
    }
  };

  const handleCreateLocation = () => {
    setLocationForm({
      name: '',
      shortName: '',
      code: '',
      type: 'RV Park',
      status: 'Active',
      address: {
        street1: '',
        street2: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      },
      contact: {
        phone: '',
        email: '',
        website: ''
      },
      timezone: 'America/New_York',
      capacity: {
        totalSites: 0,
        rvSites: 0,
        tentSites: 0,
        cabins: 0
      },
      pricing: {
        taxRate: 0
      }
    });
    setLocationDialog(true);
  };

  const handleSaveLocation = async () => {
    try {
      if (selectedLocation && tabValue === 0) {
        // Update existing location
        await api.put(
          `/locations/${selectedLocation._id}`,
          locationForm,
          {}
        );
      } else {
        // Create new location
        await api.post(
          `/locations`,
          locationForm,
          {}
        );
      }
      
      setLocationDialog(false);
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const handleCloneLocation = async (locationId: string) => {
    const name = prompt('Enter name for the new location:');
    const code = prompt('Enter code for the new location (2-5 letters):');
    
    if (name && code) {
      try {
        await api.post(
          `/locations/${locationId}/clone`,
          {
            name,
            code,
            cloneAmenities: window.confirm('Clone amenities from the original location?'),
            address: {
              street1: '',
              city: '',
              state: '',
              zipCode: ''
            },
            contact: {
              phone: '',
              email: ''
            }
          },
          {}
        );
        fetchLocations();
      } catch (error) {
        console.error('Error cloning location:', error);
      }
    }
  };

  const handleTransferSite = async () => {
    try {
      await api.post(
        `/locations/transfer-site`,
        {
          ...transferForm,
          transferDate: transferForm.transferDate.toISOString()
        }      );
      
      setTransferDialog(false);
      fetchLocationDashboard(selectedLocation._id);
    } catch (error) {
      console.error('Error transferring site:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Active':
        return <ActiveIcon color="success" />;
      case 'Inactive':
        return <InactiveIcon color="error" />;
      case 'Seasonal':
        return <ScheduleIcon color="warning" />;
      default:
        return <LocationIcon />;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUpIcon color="success" />;
      case 'decreasing':
        return <TrendingDownIcon color="error" />;
      default:
        return <StableIcon color="action" />;
    }
  };

  const renderLocationCard = (location: any) => (
    <Grid item xs={12} sm={6} md={4} key={location._id}>
      <Card 
        sx={{ 
          height: '100%',
          cursor: 'pointer',
          border: selectedLocation?._id === location._id ? 3 : 1,
          borderColor: selectedLocation?._id === location._id ? 'primary.main' : 'divider',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4
          }
        }}
        onClick={() => setSelectedLocation(location)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getStatusIcon(location.status)}
              <Box sx={{ ml: 1 }}>
                <Typography variant="h6" noWrap>
                  {location.shortName || location.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {location.code}
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={location.type} 
              size="small" 
              color={location.type === 'Resort' ? 'primary' : 'default'}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            📍 {location.address?.city || 'Unknown'}, {location.address?.state || 'Unknown'}
          </Typography>
          
          <Grid container spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" color="primary">
                  {location.capacity?.totalSites || 0}
                </Typography>
                <Typography variant="caption">
                  Sites
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" color="success.main">
                  {location.summary?.occupancy || 0}%
                </Typography>
                <Typography variant="caption">
                  Occupied
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" color="warning.main">
                  {location.capacity?.totalSites ? location.capacity.totalSites - Math.round((location.summary?.occupancy || 0) / 100 * location.capacity.totalSites) : 0}
                </Typography>
                <Typography variant="caption">
                  Available
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {location.summary?.isOpen ? (
                <Chip label="🟢 Open" size="small" color="success" variant="outlined" />
              ) : (
                <Chip label="🔴 Closed" size="small" color="error" variant="outlined" />
              )}
              {location.features?.onlineBooking && (
                <Chip label="📱" size="small" title="Online Booking" />
              )}
            </Box>
            {location.contact?.phone && (
              <Typography variant="caption" color="primary">
                📞 {location.contact.phone}
              </Typography>
            )}
          </Box>
        </CardContent>
        
        {user?.role === 'admin' && (
          <CardActions>
            <IconButton size="small" onClick={(e) => {
              e.stopPropagation();
              handleCloneLocation(location._id);
            }} title="Clone Location">
              <CopyIcon />
            </IconButton>
            <IconButton size="small" onClick={(e) => {
              e.stopPropagation();
              setSelectedLocation(location);
              setLocationForm(location);
              setLocationDialog(true);
            }} title="Edit Location">
              <EditIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    </Grid>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link color="inherit" href="#" onClick={() => setTabValue(0)}>
            Locations
          </Link>
          {selectedLocation && (
            <Typography color="text.primary">{selectedLocation.name}</Typography>
          )}
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="h4">
            Multi-Location Management
          </Typography>
          
          {user?.role === 'admin' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateLocation}
            >
              Add Location
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Locations Overview" icon={<LocationIcon />} iconPosition="start" />
          <Tab label="Comparative Analytics" icon={<AnalyticsIcon />} iconPosition="start" />
          <Tab label="Staff Management" icon={<GroupIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {/* Locations Overview */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {/* Location List/Grid */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Locations</Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, v) => v && setViewMode(v)}
                  size="small"
                >
                  <ToggleButton value="grid">
                    <DashboardIcon />
                  </ToggleButton>
                  <ToggleButton value="list">
                    <ViewListIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Seasonal">Seasonal</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    label="Type"
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="RV Park">RV Park</MenuItem>
                    <MenuItem value="Campground">Campground</MenuItem>
                    <MenuItem value="Resort">Resort</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : viewMode === 'list' ? (
                <List>
                  {locations.map((location) => (
                    <ListItem
                      key={location._id}
                      button
                      selected={selectedLocation?._id === location._id}
                      onClick={() => setSelectedLocation(location)}
                    >
                      <ListItemIcon>
                        {getStatusIcon(location.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={location.name}
                        secondary={`${location.address.city}, ${location.address.state}`}
                      />
                      <ListItemSecondaryAction>
                        <Chip label={`${location.summary?.occupancy || 0}%`} size="small" />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Grid container spacing={2}>
                  {locations.map((location) => renderLocationCard(location))}
                </Grid>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            {/* Location Dashboard */}
            {selectedLocation && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">
                    {selectedLocation.name} Dashboard
                  </Typography>
                  
                  <Stack direction="row" spacing={1}>
                    {selectedLocation.contact?.phone && (
                      <Button
                        startIcon={<PhoneIcon />}
                        size="small"
                        href={`tel:${selectedLocation.contact.phone}`}
                      >
                        Call
                      </Button>
                    )}
                    {selectedLocation.contact?.email && (
                      <Button
                        startIcon={<EmailIcon />}
                        size="small"
                        href={`mailto:${selectedLocation.contact.email}`}
                      >
                        Email
                      </Button>
                    )}
                    {user?.role === 'admin' && (
                      <Button
                        startIcon={<TransferIcon />}
                        size="small"
                        onClick={() => setTransferDialog(true)}
                      >
                        Transfer Site
                      </Button>
                    )}
                  </Stack>
                </Box>
                
                {dashboardLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress />
                  </Box>
                ) : locationDashboard ? (
                  <>
                    {/* Alerts */}
                    {locationDashboard.alerts.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        {locationDashboard.alerts.map((alert: any, index: number) => (
                          <Alert 
                            key={index} 
                            severity={alert.type}
                            sx={{ mb: 1 }}
                          >
                            {alert.message}: {alert.value}
                          </Alert>
                        ))}
                      </Box>
                    )}
                    
                    {/* Metrics Grid */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                              Occupancy
                            </Typography>
                            <Typography variant="h4">
                              {locationDashboard.metrics.occupancyRate}%
                            </Typography>
                            <Typography variant="body2">
                              {locationDashboard.metrics.occupiedSites} / {locationDashboard.metrics.totalSites}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                              Today's Revenue
                            </Typography>
                            <Typography variant="h4">
                              ${locationDashboard.metrics.todayRevenue.toFixed(2)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                              Arrivals
                            </Typography>
                            <Typography variant="h4">
                              {locationDashboard.metrics.todayArrivals}
                            </Typography>
                            <Typography variant="body2">
                              Today
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                              Departures
                            </Typography>
                            <Typography variant="h4">
                              {locationDashboard.metrics.todayDepartures}
                            </Typography>
                            <Typography variant="body2">
                              Today
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                    
                    {/* Status Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <WarningIcon color="warning" sx={{ mr: 1 }} />
                              <Typography variant="subtitle1">
                                Maintenance Issues
                              </Typography>
                            </Box>
                            <Typography variant="h6">
                              {locationDashboard.metrics.maintenanceIssues} sites need attention
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <GroupIcon color="primary" sx={{ mr: 1 }} />
                              <Typography variant="subtitle1">
                                Staff on Duty
                              </Typography>
                            </Box>
                            <Typography variant="h6">
                              {locationDashboard.metrics.staffOnDuty} employees
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                    
                    {/* Recent Activity */}
                    <Typography variant="h6" gutterBottom>
                      Recent Activity
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Guest</TableCell>
                            <TableCell>Site</TableCell>
                            <TableCell>Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {locationDashboard.recentActivity.map((activity: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Chip 
                                  label={activity.type} 
                                  size="small"
                                  color={
                                    activity.type === 'check-in' ? 'success' :
                                    activity.type === 'check-out' ? 'info' :
                                    activity.type === 'cancellation' ? 'error' :
                                    'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>{activity.guest}</TableCell>
                              <TableCell>{activity.site}</TableCell>
                              <TableCell>
                                {dayjs(activity.timestamp).format('h:mm A')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : null}
              </Paper>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Comparative Analytics */}
        {comparativeAnalytics && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Performance Comparison (Last 30 Days)
                </Typography>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Reservations</TableCell>
                        <TableCell align="right">Occupancy</TableCell>
                        <TableCell align="right">Avg Stay</TableCell>
                        <TableCell align="right">RevPAR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparativeAnalytics.locations.map((loc: any) => (
                        <TableRow key={loc.location.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LocationIcon sx={{ mr: 1 }} />
                              <Box>
                                <Typography variant="subtitle2">
                                  {loc.location.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {loc.location.code}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            ${loc.metrics.revenue.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {loc.metrics.reservations}
                          </TableCell>
                          <TableCell align="right">
                            {loc.metrics.occupancyRate.toFixed(1)}%
                          </TableCell>
                          <TableCell align="right">
                            {loc.metrics.averageStayLength} nights
                          </TableCell>
                          <TableCell align="right">
                            ${loc.metrics.revPAR}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Total
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            ${comparativeAnalytics.totals.revenue.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {comparativeAnalytics.totals.reservations}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {comparativeAnalytics.totals.averageOccupancy}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell align="right">-</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Staff Management */}
        <Alert severity="info">
          Staff management across locations coming soon
        </Alert>
      </TabPanel>

      {/* Location Dialog */}
      <Dialog
        open={locationDialog}
        onClose={() => setLocationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedLocation && tabValue === 0 ? 'Edit Location' : 'Create New Location'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location Name"
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Short Name"
                value={locationForm.shortName}
                onChange={(e) => setLocationForm({ ...locationForm, shortName: e.target.value })}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Code"
                value={locationForm.code}
                onChange={(e) => setLocationForm({ ...locationForm, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 5 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={locationForm.type}
                  label="Type"
                  onChange={(e) => setLocationForm({ ...locationForm, type: e.target.value })}
                >
                  <MenuItem value="RV Park">RV Park</MenuItem>
                  <MenuItem value="Campground">Campground</MenuItem>
                  <MenuItem value="Resort">Resort</MenuItem>
                  <MenuItem value="Marina">Marina</MenuItem>
                  <MenuItem value="Mixed">Mixed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={locationForm.status}
                  label="Status"
                  onChange={(e) => setLocationForm({ ...locationForm, status: e.target.value })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Seasonal">Seasonal</MenuItem>
                  <MenuItem value="Under Construction">Under Construction</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider>Address</Divider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={locationForm.address.street1}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  address: { ...locationForm.address, street1: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={locationForm.address.city}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  address: { ...locationForm.address, city: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="State"
                value={locationForm.address.state}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  address: { ...locationForm.address, state: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={locationForm.address.zipCode}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  address: { ...locationForm.address, zipCode: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider>Contact</Divider>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Phone"
                value={locationForm.contact?.phone || ''}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  contact: { ...locationForm.contact, phone: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={locationForm.contact?.email || ''}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  contact: { ...locationForm.contact, email: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Website"
                value={locationForm.contact?.website || ''}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  contact: { ...locationForm.contact, website: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider>Capacity</Divider>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Total Sites"
                type="number"
                value={locationForm.capacity.totalSites}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  capacity: { ...locationForm.capacity, totalSites: parseInt(e.target.value) || 0 }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="RV Sites"
                type="number"
                value={locationForm.capacity.rvSites}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  capacity: { ...locationForm.capacity, rvSites: parseInt(e.target.value) || 0 }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Tent Sites"
                type="number"
                value={locationForm.capacity.tentSites}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  capacity: { ...locationForm.capacity, tentSites: parseInt(e.target.value) || 0 }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Cabins"
                type="number"
                value={locationForm.capacity.cabins}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  capacity: { ...locationForm.capacity, cabins: parseInt(e.target.value) || 0 }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={locationForm.timezone}
                  label="Timezone"
                  onChange={(e) => setLocationForm({ ...locationForm, timezone: e.target.value })}
                >
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Rate (%)"
                type="number"
                value={locationForm.pricing.taxRate}
                onChange={(e) => setLocationForm({
                  ...locationForm,
                  pricing: { ...locationForm.pricing, taxRate: parseFloat(e.target.value) || 0 }
                })}
                inputProps={{ step: 0.01 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveLocation} variant="contained">
            {selectedLocation && tabValue === 0 ? 'Update' : 'Create'} Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Site Dialog */}
      <Dialog
        open={transferDialog}
        onClose={() => setTransferDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer Site Between Locations</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will move a site from one location to another. Future reservations will need to be handled separately.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Site Number"
                value={transferForm.siteId}
                onChange={(e) => setTransferForm({ ...transferForm, siteId: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>From Location</InputLabel>
                <Select
                  value={transferForm.fromLocationId}
                  label="From Location"
                  onChange={(e) => setTransferForm({ ...transferForm, fromLocationId: e.target.value })}
                >
                  {locations.map((loc) => (
                    <MenuItem key={loc._id} value={loc._id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>To Location</InputLabel>
                <Select
                  value={transferForm.toLocationId}
                  label="To Location"
                  onChange={(e) => setTransferForm({ ...transferForm, toLocationId: e.target.value })}
                >
                  {locations
                    .filter(loc => loc._id !== transferForm.fromLocationId)
                    .map((loc) => (
                      <MenuItem key={loc._id} value={loc._id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <DatePicker
                label="Transfer Date"
                value={transferForm.transferDate}
                onChange={(value) => value && setTransferForm({ ...transferForm, transferDate: value })}
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(false)}>Cancel</Button>
          <Button onClick={handleTransferSite} variant="contained" startIcon={<TransferIcon />}>
            Transfer Site
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Quick Actions */}
      {user?.role === 'admin' && selectedLocation && (
        <SpeedDial
          ariaLabel="Quick actions"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction
            icon={<AnalyticsIcon />}
            tooltipTitle="View Performance Report"
            onClick={() => setPerformanceDialog(true)}
          />
          <SpeedDialAction
            icon={<EditIcon />}
            tooltipTitle="Edit Location"
            onClick={() => {
              setLocationForm(selectedLocation);
              setLocationDialog(true);
            }}
          />
          <SpeedDialAction
            icon={<MapIcon />}
            tooltipTitle="View on Map"
            onClick={() => {
              // Open map view
            }}
          />
        </SpeedDial>
      )}
    </Container>
  );
};

export default LocationsPage;